import { type ColumnDef } from "@tanstack/react-table";
import type { ApiHistory } from "../types";
import { ArrowUpDown } from "lucide-react";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { DateFormats } from "@/utils/date/constants";
import { getActionVariant } from "../utils";
import ChangesDialog from "./changes-dialog";
import { ROUTES } from "@/constants/routes";
import { NavLink } from "react-router-dom";

const getResourceRouter = (resource: string, id: string) => {
  switch (resource) {
    case "dilemma":
      return ROUTES.DILEMMA_DETAILS(id);
    case "topic":
      return ROUTES.THEME_DETAILS(id);
    case "user":
      return ROUTES.USER_DETAILS(id);
    default:
      return "-";
  }
};

export const historiesColumns = (t: TFunction): ColumnDef<ApiHistory>[] => [
  {
    accessorKey: "reference",
    header: t("keys.id_reference"),
    cell: ({ row }) => {
      return (
        <NavLink
          to={getResourceRouter(
            row.original.model_type,
            row.original.object_pk,
          )}
        >
          <span className="text-gray-400 hover:text-primary hover:underline">
            {row.getValue("reference")}
          </span>
        </NavLink>
      );
    },
  },
  {
    accessorKey: "model_type",
    header: t("keys.resource"),
    cell: ({ row }) => {
      return (
        <Badge variant="secondary">
          {t(`keys.resources.${row.getValue("model_type")}`)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "action_display",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-1 cursor-pointer"
        >
          {t("keys.action")}
          <ArrowUpDown
            className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
          />
        </button>
      );
    },
    cell: ({ row }) => {
      return (
        <Badge variant={getActionVariant(row.getValue("action_display"))}>
          {t(`keys.action_types.${row.getValue("action_display")}`)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actor",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-1 cursor-pointer"
        >
          {t("keys.actor")}
          <ArrowUpDown
            className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
          />
        </button>
      );
    },
    cell: ({ row }) => {
      const actor = row.original.actor;
      return <span className="text-gray-500">{actor?.username ?? "-"}</span>;
    },
  },
  {
    accessorKey: "changes",
    header: t("keys.changes"),
    cell: ({ row }) => {
      return <ChangesDialog history={row.original} />;
    },
  },
  {
    accessorKey: "timestamp",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-1 cursor-pointer"
        >
          {t("keys.timestamp")}
          <ArrowUpDown
            className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
          />
        </button>
      );
    },
    cell: ({ row }) => {
      return (
        <span className="text-gray-500">
          {formatDate(row.getValue("timestamp"), DateFormats.UI_DATETIME_SHORT)}
        </span>
      );
    },
  },
];
